import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Link2, Baby, Sparkles, AlertTriangle, CalendarClock, Filter, X, Check, Ban, BellRing } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import { EmptyState, TableRowsSkeleton } from '../ds';

const API_URL = getApiUrl();

const REASON_META = {
  membership_expired: { label: 'Access expired', cls: 'bg-red-100 text-red-700' },
  membership_expiring: { label: 'Expiring soon', cls: 'bg-amber-100 text-amber-700' },
  postnatal_renewal: { label: 'Postnatal renewal', cls: 'bg-violet-100 text-violet-700' },
  prenatal_renewal: { label: 'Prenatal renewal', cls: 'bg-sky-100 text-sky-700' },
  prenatal_window_ended: { label: 'Prenatal ended — pitch postnatal', cls: 'bg-violet-100 text-violet-700' },
};

const HANDLED = [
  { key: 'contacted', label: 'Contacted', icon: Check },
  { key: 'renewed', label: 'Renewed', icon: Check },
  { key: 'dropped', label: 'Dropped', icon: Ban },
];

// Multi-select filter with an "is / is not" switch, so the desk can slice both ways.
const FilterGroup = ({ name, label, options, state, setState }) => {
  const cur = state[name] || { values: [], negate: false };
  const toggle = (v) => {
    const values = cur.values.includes(v) ? cur.values.filter((x) => x !== v) : [...cur.values, v];
    setState({ ...state, [name]: { ...cur, values } });
  };
  return (
    <div className="min-w-[180px]" data-testid={`renewal-filter-${name}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <button type="button" onClick={() => setState({ ...state, [name]: { ...cur, negate: !cur.negate } })}
          data-testid={`renewal-filter-${name}-op`}
          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cur.negate ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
          {cur.negate ? 'is not' : 'is'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => (
          <button key={o.value} type="button" onClick={() => toggle(o.value)}
            data-testid={`renewal-filter-${name}-${o.value}`}
            className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
              cur.values.includes(o.value) ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const Chip = ({ on, onLabel, offLabel, Icon, testid }) => (
  <span data-testid={testid}
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
    <Icon className="w-3 h-3" /> {on ? onLabel : offLabel}
  </span>
);

export const RenewalsPanel = ({ onOpenLead, onSendRenewalLink }) => {
  const [data, setData] = useState({ rows: [], summary: {}, countries: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [q, setQ] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v?.values?.length) { p[k] = v.values.join(','); if (v.negate) p[`${k}_op`] = 'nin'; }
    });
    if (q.trim()) p.q = q.trim();
    return p;
  }, [filters, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/renewals`, { params, withCredentials: true });
      setData({ rows: res.data.rows || [], summary: res.data.summary || {}, countries: res.data.countries || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load renewals');
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const mark = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/renewals/${id}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const s = data.summary;
  const typeValues = filters.type?.values || [];
  const showSoulai = !typeValues.length || typeValues.includes('prenatal');
  const showBabyTracker = !typeValues.length || typeValues.includes('postnatal');

  return (
    <div className="card" data-testid="renewals-panel">
      <div className="card-header flex flex-wrap items-center justify-between gap-3 py-4">
        <div>
          <h2 className="font-display text-base md:text-lg font-semibold">Renewals &amp; upsells</h2>
          <p className="text-xs text-muted-foreground">
            Customers whose access is ending, who have moved to postnatal, or who never bought Till-Delivery.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…"
            className="input-base text-sm py-1.5 w-56" data-testid="renewals-search" />
          <button type="button" onClick={() => setShowFilters((v) => !v)} data-testid="renewals-toggle-filters"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <Filter className="w-3.5 h-3.5" /> Filters
          </button>
          <button type="button" onClick={load} data-testid="renewals-refresh"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Revenue-at-risk strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 px-4 pt-4" data-testid="renewals-summary">
        {[
          { k: 'expired', label: 'Access expired', cls: 'text-red-700' },
          { k: 'expiring', label: 'Expiring ≤30d', cls: 'text-amber-700' },
          { k: 'postnatal_due', label: 'Postnatal due', cls: 'text-violet-700' },
          { k: 'prenatal_due', label: 'Prenatal due', cls: 'text-sky-700' },
          { k: 'soulai_upsell', label: 'No SoulAI', cls: 'text-indigo-700' },
          { k: 'baby_tracker_upsell', label: 'No Baby Tracker', cls: 'text-emerald-700' },
        ].map((t) => (
          <div key={t.k} className="rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`renewals-stat-${t.k}`}>
            <div className="text-[11px] text-slate-500">{t.label}</div>
            <div className={`text-lg font-bold ${t.cls}`}>{s[t.k] ?? 0}</div>
          </div>
        ))}
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-5 px-4 py-4 border-b border-slate-100" data-testid="renewals-filters">
          <FilterGroup name="type" label="Programme" state={filters} setState={setFilters}
            options={[{ value: 'prenatal', label: 'Prenatal' }, { value: 'postnatal', label: 'Postnatal' }]} />
          <FilterGroup name="reason" label="Why" state={filters} setState={setFilters}
            options={Object.entries(REASON_META).map(([value, m]) => ({ value, label: m.label }))} />
          {showSoulai && (
            <FilterGroup name="soulai" label="SoulAI access" state={filters} setState={setFilters}
              options={[{ value: 'yes', label: 'Has SoulAI' }, { value: 'no', label: 'No SoulAI' }]} />
          )}
          {showBabyTracker && (
            <FilterGroup name="baby_tracker" label="Baby Tracker" state={filters} setState={setFilters}
              options={[{ value: 'yes', label: 'Has tracker' }, { value: 'no', label: 'No tracker' }]} />
          )}
          <FilterGroup name="trial_paid" label="Trial" state={filters} setState={setFilters}
            options={[{ value: 'yes', label: 'Trial paid' }, { value: 'no', label: 'Trial unpaid' }]} />
          <FilterGroup name="handled" label="Status" state={filters} setState={setFilters}
            options={[{ value: 'open', label: 'Open' }, { value: 'contacted', label: 'Contacted' },
                      { value: 'renewed', label: 'Renewed' }, { value: 'dropped', label: 'Dropped' }]} />
          {data.countries.length > 0 && (
            <FilterGroup name="country" label="Country" state={filters} setState={setFilters}
              options={data.countries.map((c) => ({ value: c, label: c }))} />
          )}
          {Object.keys(filters).length > 0 && (
            <button type="button" onClick={() => setFilters({})} data-testid="renewals-clear-filters"
              className="self-end inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-200">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      )}

      {loading ? (
        <TableRowsSkeleton rows={6} cols={5} />
      ) : data.rows.length === 0 ? (
        <div className="p-6">
          <EmptyState icon={CalendarClock} title="Nothing to renew right now"
            description="Customers appear here when access is ending, the EDD has passed, or Till-Delivery was never bought." />
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="renewals-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Why now</th>
              <th className="px-4 py-2.5">Dates</th>
              <th className="px-4 py-2.5">Upsell signals</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r) => (
              <tr key={r.renewal_id} className="hover:bg-slate-50/70" data-testid={`renewal-row-${r.renewal_id}`}>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => r.lead_id && onOpenLead?.(r.lead_id)}
                    className="font-semibold text-indigo-600 hover:underline" data-testid={`renewal-open-lead-${r.renewal_id}`}>
                    {r.lead_name || r.email || r.phone || 'Unknown'}
                  </button>
                  <div className="text-xs text-slate-500">{r.phone || r.email}</div>
                  <div className="text-[11px] text-slate-400">{r.country}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${r.renewal_type === 'postnatal' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`}
                      data-testid={`renewal-type-${r.renewal_id}`}>{r.renewal_type}</span>
                    {(r.reasons || []).map((x) => (
                      <span key={x} data-testid={`renewal-reason-${x}-${r.renewal_id}`}
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${REASON_META[x]?.cls || 'bg-slate-100 text-slate-600'}`}>
                        {REASON_META[x]?.label || x}
                      </span>
                    ))}
                    {r.access_blocked && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                        <AlertTriangle className="w-3 h-3" /> Blocked in app
                      </span>
                    )}
                    {r.open_enquiry && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700"
                        data-testid={`renewal-open-enquiry-${r.renewal_id}`}
                        title="She tapped “Talk to our team” in the app — no associate name was shown to her. Handle it in Leads → Requests.">
                        <BellRing className="w-3 h-3" />
                        She asked for a call{r.open_enquiry.status === 'contacted' ? ' · contacted' : ''}
                        {r.open_enquiry.repeat_count > 0 ? ` · ×${r.open_enquiry.repeat_count + 1}` : ''}
                      </span>
                    )}
                    {r.expiry_reason === 'prenatal_window_ended' && (
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700"
                        data-testid={`renewal-prenatal-ended-${r.renewal_id}`}
                        title="Account still active — only prenatal courses and classes are locked. Postnatal products keep working.">
                        Prenatal ended (EDD+7) · postnatal upsell
                      </span>
                    )}
                    {r.expiry_reason === 'admin_suspended' && (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700"
                        data-testid={`renewal-admin-suspended-${r.renewal_id}`}>
                        Suspended by {r.suspended_by || 'LMS admin'}
                      </span>
                    )}
                    {r.restored_reason && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                        data-testid={`renewal-restored-${r.renewal_id}`}>
                        Access restored · {String(r.restored_reason).replace(/_/g, ' ')}
                      </span>
                    )}
                    {r.date_source === 'lms_admin' && (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                        data-testid={`renewal-date-source-${r.renewal_id}`}>
                        Date set in LMS
                      </span>
                    )}
                  </div>
                  {r.handled_status && r.handled_status !== 'open' && (
                    <div className="mt-1 text-[11px] font-semibold text-slate-500 capitalize" data-testid={`renewal-handled-${r.renewal_id}`}>
                      {r.handled_status}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {r.course_expiry_date && (
                    <div>Access till <b>{r.course_expiry_date}</b>
                      {r.days_left != null && (
                        <span className={r.days_left < 0 ? 'text-red-600 font-semibold' : 'text-amber-600'}>
                          {' '}({r.days_left < 0 ? `${Math.abs(r.days_left)}d ago` : `${r.days_left}d left`})
                        </span>
                      )}
                    </div>
                  )}
                  {r.edd && <div className="text-slate-500">EDD {r.edd}</div>}
                  {Object.entries(r.reminders || {}).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`renewal-reminders-${r.renewal_id}`}>
                      {Object.entries(r.reminders).map(([k, v]) => (
                        <span key={k}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v?.delivered ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}
                          data-testid={`renewal-reminder-${v?.offset_days}-${r.renewal_id}`}>
                          LMS D-{v?.offset_days} {v?.delivered ? 'reminder sent' : 'not delivered'}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <Chip on={r.soulai_access} onLabel="SoulAI" offLabel="No SoulAI" Icon={Sparkles} testid={`renewal-soulai-${r.renewal_id}`} />
                    <Chip on={r.baby_tracker_access} onLabel="Tracker" offLabel="No tracker" Icon={Baby} testid={`renewal-tracker-${r.renewal_id}`} />
                    <Chip on={r.trial_paid} onLabel="Trial paid" offLabel="Trial unpaid" Icon={Check} testid={`renewal-trial-${r.renewal_id}`} />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    <button type="button" onClick={() => r.lead_id && onSendRenewalLink?.(r.lead_id)}
                      data-testid={`renewal-send-link-${r.renewal_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
                      <Link2 className="w-3.5 h-3.5" /> Renewal link
                    </button>
                    {HANDLED.map((h) => (
                      <button key={h.key} type="button" onClick={() => mark(r.renewal_id, h.key)}
                        data-testid={`renewal-mark-${h.key}-${r.renewal_id}`}
                        className="rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
                        {h.label}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RenewalsPanel;
